import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion, useTransform, Variants } from 'framer-motion';
import { SectionEyebrow } from './SectionEyebrow';
import { GridRails } from './GridRails';
import { Reveal } from './Reveal';
import { useAutoAdvance } from '../hooks/useAutoAdvance';
import { ecosystemPillars, Venture, ECOSYSTEM_SELECT_EVENT } from '../data/ecosystem';
const LINE = 'border-mist-line';
// Frame = the vertical page rails; cell padding puts text on the page's content edge
const FRAME = 'mx-auto max-w-[1720px] px-5 md:px-16 lg:px-24';
const LEFT = 'px-4 md:pl-8 md:pr-16';
// Divider sits 32px left of centre so the right cell's text starts exactly at centre
const RIGHT = `px-4 md:-ml-8 md:border-l md:pl-8 md:pr-8 ${LINE}`;
const SLIDE_MS = 9000;
const EASE = [0.23, 1, 0.32, 1] as const;
const RING_R = 15;
const RING_C = 2 * Math.PI * RING_R;
function Rule() {
  return <div aria-hidden="true" className={`relative z-[6] w-full border-t ${LINE}`} data-id="element-84" data-mp="28ts723iq6t21" />;
}

/** Same feel as the scroll-in reveal: fade, small rise, de-blur — staggered per child. */
// All slides are stacked in one grid cell, so the section is always as tall as the tallest
// slide and never resizes while switching. The incoming slide waits for the outgoing one to fade.
const group: Variants = {
  enter: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.26
    }
  },
  exit: {
    transition: {
      staggerChildren: 0.03,
      staggerDirection: -1
    }
  }
};
const item: Variants = {
  enter: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.5,
      ease: EASE
    }
  },
  exit: {
    opacity: 0,
    y: -6,
    filter: 'blur(4px)',
    transition: {
      duration: 0.22,
      ease: 'easeOut'
    },
    transitionEnd: {
      y: 12
    }
  }
};
const still: Variants = {
  enter: {
    opacity: 1,
    transition: {
      duration: 0
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0
    }
  }
};
const STACK = '[grid-area:1/1]';

/** The ecosystem: three pillar slides that swap in place, with auto-advance and manual controls. */
export function EcosystemSection() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, {
    amount: 0.35
  });
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  useEffect(() => {
    const onVis = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);
  const count = ecosystemPillars.length;
  const go = useCallback((i: number) => setIndex((i % count + count) % count), [count]);

  // Pillar tiles elsewhere on the page pick the matching slide
  useEffect(() => {
    const onSelect = (e: Event) => {
      const name = (e as CustomEvent<string>).detail;
      const i = ecosystemPillars.findIndex(pl => pl.short.toLowerCase() === String(name).toLowerCase());
      if (i >= 0) setIndex(i);
    };
    window.addEventListener(ECOSYSTEM_SELECT_EVENT, onSelect);
    return () => window.removeEventListener(ECOSYSTEM_SELECT_EVENT, onSelect);
  }, []);
  const next = useCallback(() => setIndex(i => (i + 1) % count), [count]);
  const paused = !!reduce || !inView || hovered || focused || tabHidden;
  const progress = useAutoAdvance(index, SLIDE_MS, paused, next);
  const dashOffset = useTransform(progress, p => RING_C * (1 - p));
  const v = reduce ? still : item;
  return <section ref={ref} id="ecosystem" aria-roledescription="carousel" aria-label="The ecosystem" className="relative bg-white pb-[48px] pt-[48px] text-ink md:pb-[80px] md:pt-[72px]" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocus={() => setFocused(true)} onBlur={e => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocused(false);
  }} data-id="element-85" data-mp="20xamarklakrk">
      <GridRails data-id="element-86" data-mp="3i1z9res8g0t2" />

      <div className="relative z-10" data-id="element-87" data-mp="3i3k5v8u73rt1">
        {/* Label row */}
        <div className={FRAME} data-id="element-88" data-mp="3n12dmvwh80zg">
          <div className="grid md:grid-cols-2" data-id="element-89" data-mp="3n00t80tkm252">
            <div className={`${LEFT} hidden md:block`} data-id="element-90" data-mp="38cfraa8fm8yq" />
            <Reveal className={`${RIGHT} pb-5 md:border-l-0`} data-id="element-91" data-mp="38bwjvnger2yy">
              <SectionEyebrow label="THE ECOSYSTEM" data-id="element-92" data-mp="3db0py581ckl2" />
            </Reveal>
          </div>
        </div>
        <Rule data-id="element-93" data-mp="2zb81yhnw8igs" />

        {/* Pillar | ventures */}
        <div className={FRAME} data-id="element-94" data-mp="2zdubuny9ba26">
          <div className="grid md:grid-cols-2" data-id="element-95" data-mp="34aksbcdz47o4">
            <div className={`${LEFT} flex flex-col py-8 md:py-10`} data-id="element-96" data-mp="34cfbom9ljrq3">
              <div className="grid" aria-live="polite" data-id="element-97" data-mp="2ped9afeaxcaf">
                {ecosystemPillars.map((p, i) => {
                const active = i === index;
                return <motion.div key={p.short} variants={group} initial={false} animate={active ? 'enter' : 'exit'} aria-hidden={!active} className={`${STACK} ${active ? '' : 'pointer-events-none'}`} data-id="element-98" data-mp="nmgo0e2c0o2g" data-mpi={i}>
                      <motion.div variants={v} className="flex items-center justify-between font-accent text-[11px] uppercase tracking-[0.08em] text-ink/55" data-id="element-99" data-mp="2862hyqfowm4r">
                        <span data-id="element-100" data-mp="2vttt3qphut4g">{p.numeral}</span>
                        <span data-id="element-101" data-mp="2vsiq3t3vhpee">{String(p.ventures.length).padStart(2, '0')} ventures</span>
                      </motion.div>
                      <motion.h2 variants={v} className="mt-6 max-w-[520px] font-display text-[40px] leading-[1.02] tracking-[-0.025em] md:text-[52px] lg:text-[60px]" data-id="element-102" data-mp="2rryrsm7kcfqo">
                        {p.title}
                      </motion.h2>
                      <motion.p variants={v} className="mt-5 max-w-[400px] text-[14.5px] leading-[1.45] text-ink/65" data-id="element-103" data-mp="1bg31m9tasjbm">
                        {p.intro}
                      </motion.p>
                    </motion.div>;
              })}
              </div>

              {/* Controls */}
              <Reveal delay={0.12} className="mt-10 md:mt-auto md:pt-12" data-id="element-104" data-mp="3bi2uz0h2vr1j">
                <div className="flex items-center gap-5" data-id="element-105" data-mp="3fga3j81e1m1n">
                  {/* Progress ring with prev / next */}
                  <div className="flex items-center gap-1" data-id="element-106" data-mp="3ketob548wrsf">
                    <button type="button" onClick={() => go(index - 1)} aria-label="Previous pillar" className="grid h-9 w-9 place-items-center text-ink/45 transition-colors duration-200 hover:text-ink focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink/40" data-id="element-107" data-mp="3pey2upllfcg0">
                      <Chevron dir="left" data-id="element-108" data-mp="c1w82l98ih2z" />
                    </button>
                    <div className="relative h-10 w-10" aria-hidden="true" data-id="element-109" data-mp="gzy03von1qql">
                      <svg viewBox="0 0 40 40" className="h-10 w-10 -rotate-90" data-id="element-110" data-mp="gy39q1ull6f4">
                        <circle cx="20" cy="20" r={RING_R} fill="none" className="stroke-ink/10" strokeWidth={1} data-id="element-111" data-mp="r5fpgp3hjma0" />
                        <motion.circle cx="20" cy="20" r={RING_R} fill="none" className="stroke-ink" strokeWidth={1.2} strokeDasharray={RING_C} style={{
                        strokeDashoffset: reduce ? 0 : dashOffset
                      }} data-id="element-112" data-mp="m74da7ppwzom" />
                      </svg>
                      <span className="absolute inset-0 grid place-items-center font-accent text-[10px] tabular-nums text-ink/80" data-id="element-113" data-mp="103rggs89km2z">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <button type="button" onClick={() => go(index + 1)} aria-label="Next pillar" className="grid h-9 w-9 place-items-center text-ink/45 transition-colors duration-200 hover:text-ink focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink/40" data-id="element-114" data-mp="1smyetb1yu1br">
                      <Chevron dir="right" data-id="element-115" data-mp="1xi567kfxqbsj" />
                    </button>
                  </div>

                  {/* Pillar picker */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-l border-mist-line pl-5" role="group" aria-label="Choose pillar" data-id="element-116" data-mp="2q96se5i1p7lk">
                    {ecosystemPillars.map((pl, i) => {
                    const active = i === index;
                    return <button key={pl.short} type="button" onClick={() => go(i)} aria-pressed={active} className={`relative flex items-center gap-2 py-1 font-accent text-[11px] uppercase tracking-[0.08em] transition-colors duration-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-ink/40 ${active ? 'text-ink' : 'text-ink/40 hover:text-ink/75'}`} data-id="element-117" data-mp="2l9kbnm30d980" data-mpi={i}>
                          <span aria-hidden="true" className={`inline-block h-[6px] w-[6px] transition-colors duration-200 ${active ? 'bg-ink' : 'border border-ink/30'}`} data-id="element-118" data-mp="3t0zoysf082ar" />
                          {pl.short}
                        </button>;
                  })}
                  </div>
                </div>
              </Reveal>
            </div>

            <div className={`${RIGHT} border-t !px-0 md:border-t-0`} data-id="element-119" data-mp="39bfkfu5y9g8j">
              <div className="grid" data-id="element-120" data-mp="2lmmwajvkkcoh">
                {ecosystemPillars.map((p, i) => {
                const active = i === index;
                return <motion.ul key={p.short} variants={group} initial={false} animate={active ? 'enter' : 'exit'} aria-hidden={!active} className={`${STACK} ${active ? '' : 'pointer-events-none'}`} data-id="element-121" data-mp="3pfz76jb3v3x0" data-mpi={i}>
                      {p.ventures.map((ven, vi) => <motion.li key={ven.name} variants={v} className={vi > 0 ? `border-t ${LINE}` : ''} data-id="element-122" data-mp="36gv0hh71dm8i" data-mpi={vi}>
                          <VentureRow venture={ven} focusable={active} data-id="element-123" data-mp="31otsw8ktcjot" />
                        </motion.li>)}
                    </motion.ul>;
              })}
              </div>
            </div>
          </div>
        </div>
        <Rule data-id="element-124" data-mp="2nq3t2l6m05wr" />
      </div>
    </section>;
}
function VentureRow({
  venture: v,
  focusable
}: {
  venture: Venture;
  focusable: boolean;
}) {
  const body = <div className="venture-body pr-8" data-id="element-125" data-mp="2iriv6setxybh">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-126" data-mp="y8yix3cztkzv">
        <h3 className="font-display text-[24px] tracking-[-0.02em] md:text-[28px]" data-id="element-127" data-mp="y8q7lfwiqnqs">{v.name}</h3>
        {v.meta && <span className="border border-ink/25 px-2.5 py-1 font-accent text-[10px] uppercase tracking-[0.08em] text-ink/80" data-id="element-128" data-mp="139w2nkdnin3h">
            {v.meta}
          </span>}
      </div>
      <p className="venture-text mt-2 max-w-[440px] text-[14.5px] leading-[1.45] text-ink/65" data-id="element-129" data-mp="1c6n5cfmpl071">{v.body}</p>
    </div>;
  const cell = 'relative block px-4 py-6 md:py-7 md:pl-8 md:pr-8';
  if (!v.url) return <div className={cell} data-id="element-130" data-mp="kfh2vsjimqel">{body}</div>;
  return <a href={v.url} target="_blank" rel="noopener noreferrer" tabIndex={focusable ? undefined : -1} aria-label={`${v.name} — opens in a new tab`} className={`venture-link group ${cell} focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-ink/40`} data-id="element-131" data-mp="oiwycljumkpp">
      <svg aria-hidden="true" viewBox="0 0 12 12" className="venture-arrow absolute right-4 top-7 h-[11px] w-[11px] text-ink md:right-8 md:top-8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" strokeLinejoin="miter" data-id="element-132" data-mp="rrii6dwrsfe">
        <path d="M3 9L9 3M4 3h5v5" data-id="element-133" data-mp="1dw46z3vq9pfk" />
      </svg>
      {body}
    </a>;
}
function Chevron({
  dir
}: {
  dir: 'left' | 'right';
}) {
  return <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={1.3} strokeLinecap="square" aria-hidden="true" data-id="element-134" data-mp="l4udr7k6eij9">
      {dir === 'left' ? <path d="M7.5 2.5L4 6l3.5 3.5" data-id="element-135" data-mp="u6c037o6fgge" /> : <path d="M4.5 2.5L8 6l-3.5 3.5" data-id="element-136" data-mp="u6kc6xch6hqj" />}
    </svg>;
}